import './external-accounts-associations.scss';
import {useEffect, useState} from 'react';
import {Box, Button} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {translations} from '../../i18n/translations';
import {ExternalAccount} from '../../backend/interfaces/external-account';
import {ExternalAccountsRepository} from '../../repositories/external-accounts';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {PageHeader} from '../page-header/page-header';
import DeleteResourceConfirmationModal from '../delete-resource-confirmation-modal';
import {getExternalAccountTypeLabel} from '../../utils/functions/get-external-account-type-label';

// Columns IDs
enum ColumnIdentifier {
  Type = 'type',
  DeleteAccount = 'delete-account',
}

/**
 * Listing of all the external accounts associated with a user.
 *
 * Only system administrators can access this listing.
 */
export const ExternalAccountsAssociations = (props: {
  userId: string;
  associateAccountUrl: string;
  accountRowBaseUrl: string;
}) => {
  const snackBarContext = useSnackBar();
  const {t} = useTranslation();

  const [accounts, setAccounts] = useState<Array<ExternalAccount<any>> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [accountToDelete, setAccountToDelete] = useState<{type: string; label: string} | undefined>(
    undefined
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Fetch the accounts
  const handleGetAccounts = async () => {
    setIsLoading(true);
    setError(undefined);

    try {
      const result: Array<ExternalAccount<any>> =
        await ExternalAccountsRepository.instance.getForUser(props.userId);

      setAccounts(result);
    } catch (error) {
      console.debug('🔴 Could not get the external accounts.', error);
      setError(t(translations.external_accounts.list.error_message));
    } finally {
      setIsLoading(false);
    }
  };

  // On mount
  useEffect(() => {
    handleGetAccounts();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userId]);

  // Ask to delete the account
  const handleAskDeleteAccount = (id: string) => {
    const account = accounts?.find((obj) => obj.id === id);

    if (account) {
      setAccountToDelete({
        type: account.type,
        label: getExternalAccountTypeLabel(account.type),
      });
    }
  };

  // Delete the account
  const handleDeleteAccount = async () => {
    if (!accountToDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await ExternalAccountsRepository.instance.delete(props.userId, accountToDelete.type as any);

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.external_accounts.list.delete_account_success_message, {
          type: accountToDelete.label,
        }),
      });

      console.debug('🟢 External account deleted.', {
        userId: props.userId,
        type: accountToDelete.type,
      });

      // Refresh the list
      handleGetAccounts();
    } catch (error) {
      console.debug('🔴 Could not delete the external account.', error);

      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.external_accounts.list.delete_account_error_message, {
          type: accountToDelete.label,
        }),
      });
    } finally {
      setIsDeleting(false);
      setAccountToDelete(undefined);
    }
  };

  // Process data for the table
  const data: PaginatedTableData<ExternalAccount<any>> = (accounts || []).map((obj) => {
    return {
      id: obj.id || '',
      item: obj,
      toLink: `${props.accountRowBaseUrl}/${obj.type}`,
      columns: [
        {
          columnId: ColumnIdentifier.Type,
          value: getExternalAccountTypeLabel(obj.type),
        },
        {
          columnId: ColumnIdentifier.DeleteAccount,
        },
      ],
    };
  });

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <PageHeader
        rightButtons={
          <Button
            variant="contained"
            color="primary"
            endIcon={<AddIcon />}
            component={Link}
            to={props.associateAccountUrl}
          >
            {t(translations.external_accounts.list.associate_account_button)}
          </Button>
        }
      />
      <Box className="external-accounts-listing-container">
        <Box className="external-accounts-listing-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.external_accounts.list.no_accounts)}
            columns={[
              {
                columnId: ColumnIdentifier.Type,
                title: t(translations.external_accounts.list.type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.DeleteAccount,
                title: t(translations.external_accounts.list.delete_account_header),
                flexGrow: 1,
                button: {
                  type: 'text-button',
                  showColumnValue: false,
                  textButton: {
                    label: t(translations.external_accounts.list.delete_account_button),
                    color: 'error',
                    handleClick: handleAskDeleteAccount,
                  },
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={1}
            currentPage={1}
            handlePageChange={() => {}}
            linkableRows={true}
          />
        </Box>
      </Box>
      <DeleteResourceConfirmationModal
        id="confirm-delete-external-account-dialog"
        open={!!accountToDelete}
        title={t(translations.external_accounts.list.delete_account_dialog_title, {
          type: accountToDelete?.label,
        })}
        description={t(translations.external_accounts.list.delete_account_dialog_description)}
        cannotBeUndoneMessage={t(
          translations.external_accounts.list.delete_account_dialog_cannot_be_undone
        )}
        confirm_label={t(translations.external_accounts.list.delete_account_dialog_confirm_button)}
        cancel_label={t(translations.external_accounts.list.delete_account_dialog_cancel_button)}
        isDeleting={isDeleting}
        onCancel={() => setAccountToDelete(undefined)}
        onConfirm={handleDeleteAccount}
      />
    </Box>
  );
};
